import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExceptionDetail } from 'types';

export const defaultApi = axios.create({
  responseType: 'json',
});

defaultApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const { status, data } = error.response;
    return Promise.reject(
      new ApiError(status, data.title, data.detail, data.exceptionDetails)
    );
  }
);

export class ApiError extends Error {
  public readonly title: string;
  public readonly detail: string;
  public readonly statusCode: number;
  public readonly exceptionDetails?: ExceptionDetail[];

  constructor(statusCode: number, title: string, detail: string, exceptionDetails?: ExceptionDetail[]) {
    super(detail);

    Object.setPrototypeOf(this, ApiError.prototype);

    this.title = title;
    this.detail = detail;
    this.statusCode = statusCode;
    this.exceptionDetails = exceptionDetails;
  }
}

export const useDefaultErrorHandler = () => {
  const navigate = useNavigate();

  const defaultErrorHandler = useCallback(
    (error: ApiError): void => {
      switch (error.statusCode) {
        case 404:
          navigate('/error/404');
          break;
        default:
          navigate('/error/500', { state: { ...error } });
      }
    },
    [navigate]
  );
  return defaultErrorHandler;
};

const responseBody = (response: AxiosResponse) => response.data;

export const requests = {
  get: <T>(url: string, config?: AxiosRequestConfig | undefined): Promise<T> =>
    defaultApi.get(url, config).then(responseBody),
  post: <T>(url: string, body: unknown): Promise<T> =>
    defaultApi.post(url, body).then(responseBody),
  put: <T>(url: string, body: unknown): Promise<T> =>
    defaultApi.put(url, body).then(responseBody),
  patch: <T>(url: string, body: unknown): Promise<T> =>
    defaultApi.patch(url, body).then(responseBody),
  delete: <T>(url: string): Promise<T> =>
    defaultApi.delete(url).then(responseBody),
};
